import PreloadHandler, { hashTerm } from './PreloadHandler';

const VARIABLE = /(SELECT\s+)(\?\S+)/;
const QUERY_TAIL = /\}[^}]*$/;

/**
 * Given a number of paths, preloads them.
 * This handler can be used for template layers that use tagged template literals.
 * Such as uHTML and Preact + htm.
 */
export default class BundleHandler extends PreloadHandler {
  /**
   * Creates a preload function.
   */
  handle(pathData, pathProxy) {
    const _this = this;

    return async (paths, isMap = false) => {
      const pathExpressions = await (await Promise.all(paths.map(path => path.pathExpression)));
      const mergedPathExpressions = pathExpressions.map(pathExpression => pathExpression
        .filter((segment, index) => {
          if (segment.predicate && isMap && index === 1)
            return false;
          return segment.predicate;
        })
        .map(segment => segment.predicate.value)
        .map(predicate => predicate.startsWith('http') ? `<${predicate}>` : predicate)
        .join(' / ')
      );

      const resultsCaches = await _this.createResultsCache(pathExpressions, mergedPathExpressions, pathData, pathProxy, isMap);

      for (const [index, path] of paths.entries()) {
        const predicateString = (await path.predicate)?.value;
        const filters = await (path.path).filters;
        const key = `${predicateString}|${filters?.map(filter => filter.toString('')) ?? ''}`;
        const cacheKey = mergedPathExpressions[index];

        if (predicateString) {
          const cacheItems = resultsCaches.map(resultsCache => resultsCache.path.propertyCache[cacheKey]);

          const matches = [];
          for (const cacheItem of cacheItems) {
            for (const cacheItemValue of cacheItem) {
              const itemKey = `${predicateString}|${cacheItemValue.path.filters?.map(filter => filter.toString('')) ?? ''}`;
              // eslint-disable-next-line max-depth
              if (key === itemKey)
                matches.push(cacheItemValue);
            }
          }

          path.path.resultsCache = matches;
        }
      }

      return pathProxy;
    };
  }

  /**
   * Creates a cache for the results of
   * resolving the given predicates against the path.
   */
  async createResultsCache(pathExpressions, mergedPathExpressions, pathData, path, isMap = false) {
    // Execute the preloading query
    const { query, vars, resultVar } = await this.createQuery(pathExpressions, mergedPathExpressions, path, isMap);
    const { settings: { queryEngine } } = pathData;
    const bindings = queryEngine.execute(query);

    const predicates = mergedPathExpressions;

    const mergedFilters = pathExpressions
      .map(pathExpression => pathExpression.filter(segment => segment.filters).map(segment => segment.filters));

    // Extract all results and their preloaded property values
    const resultsCache = {};
    const propertyCaches = {};
    for await (const binding of bindings) {
      // Initialize the result's cache if needed
      const result = binding.get(resultVar) ?? binding.get(resultVar.substring(1)), hash = hashTerm(result);

      if (!(hash in resultsCache)) {
        // Create the property cache
        const propertyCache = propertyCaches[hash] = {};
        for (const predicate of predicates)
          propertyCache[predicate] = [];
        // Create the result path
        const resultData = { subject: result, propertyCache };
        resultsCache[hash] = pathData.extendPath(resultData, null);
      }

      // Create and cache a possible property value path from the binding
      const propertyCache = propertyCaches[hash];
      for (let i = 0; i < vars.length; i++) {
        const value = binding.get(vars[i]) ?? binding.get(vars[i].substring(1));
        if (value) {
          const valuePath = pathData.extendPath({ subject: value, filters: mergedFilters[i] }, null);
          propertyCache[predicates[i]].push(valuePath);
        }
      }
    }
    return Object.values(resultsCache);
  }

  async createQuery(pathExpressions, mergedPathExpressions, path, isMap = false) {
    const subject = `${await path.subject}`;
    path.finalClause = isMap ? () => ' ' : variable => `VALUES ${variable} { <${subject}> }`;
    const parentQuery = await path.sparql;
    const variableMatch = VARIABLE.exec(parentQuery);
    const resultVar = variableMatch[2];

    const vars = mergedPathExpressions.map((p, i) => `?preload_${i}`);

    const mergedFilters = pathExpressions.map((pathExpression, index) => pathExpression
      .filter(segment => segment.filters)
      .map(segment => segment.filters.map(filter => filter.toString(vars[index])))
    );

    const filters = mergedFilters.map(mergedFilter => mergedFilter.length ? `  FILTER(${mergedFilter.join(' || ')})` : '');
    const preloadClauses = mergedPathExpressions.map((predicate, i) => `    { ${resultVar} ${predicate} ${vars[i]}. \n    ${filters[i]}\n    }`).join('\n    UNION\n');
    const newQueryTail = `  OPTIONAL {\n${preloadClauses}\n  }\n$&`;
    const query = parentQuery.replace(VARIABLE, `$1$2 ${vars.join(' ')}`).replace(QUERY_TAIL, newQueryTail);

    return {
      query,
      vars,
      resultVar,
    };
  }
}
